import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, KeyRound, ShieldOff, ShieldCheck, Trash2, RotateCcw, X, ExternalLink, ChevronRight, UserCog } from 'lucide-react';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, LoadingState,
} from '../components/ui';

interface User {
  userId?: string;
  id?: string;
  email: string;
  role: string;
  entityRole?: string;
  entityId: string;
  fullName?: string;
  isActive?: boolean;
  suspendedAt?: string | null;
  deletedAt?: string | null;
  onboardingCompletedAt?: string | null;
}

interface Entity {
  id: string;
  name: string;
}

const ROLE_VARIANT: Record<string, 'info' | 'warning' | 'default'> = {
  admin:           'warning',
  executive:       'info',
  product_manager: 'default',
};

function ActionButton({
  onClick, disabled, loading, icon: Icon, label, variant = 'default',
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon: React.ElementType;
  label: string;
  variant?: 'default' | 'danger' | 'success';
}) {
  const base = 'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-40';
  const variants = {
    default: 'text-gray-300 hover:bg-white/[0.06] hover:text-white',
    danger:  'text-red-400 hover:bg-red-900/20 hover:text-red-300',
    success: 'text-green-400 hover:bg-green-900/20 hover:text-green-300',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${variants[variant]}`}>
      {loading ? (
        <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      {label}
    </button>
  );
}

function ConfirmDialog({
  title, body, confirmLabel, onConfirm, onCancel, loading, danger,
}: {
  title: string; body: string; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void;
  loading?: boolean; danger?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-bg-surface border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-2xl z-10">
        <h3 className="font-semibold text-white mb-2">{title}</h3>
        <p className="text-sm text-gray-400 mb-5">{body}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm bg-white/[0.06] text-gray-300 hover:bg-white/10 transition-colors">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${
              danger ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-primary text-white hover:bg-primary/90'
            }`}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserActions({ user, onDone }: { user: User; onDone: () => void }) {
  const qc = useQueryClient();
  const userId = user.userId ?? user.id ?? '';
  const entityId = user.entityId ?? '';
  const [confirm, setConfirm] = useState<'suspend' | 'unsuspend' | 'remove' | null>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [rolePending, setRolePending] = useState<string | null>(null);

  const isSuspended = Boolean(user.suspendedAt) || user.isActive === false;
  const isDeleted = Boolean(user.deletedAt);
  const currentRole = user.entityRole ?? user.role ?? 'member';

  const resetMutation = useMutation({
    mutationFn: () => api.post<{ resetLink: string }>(`/users/${entityId}/${userId}/reset-password`),
    onSuccess: (data) => setResetResult(data.resetLink),
  });

  const suspendMutation = useMutation({
    mutationFn: () => api.post(`/users/${entityId}/${userId}/suspend`),
    onSuccess: () => { setConfirm(null); void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); onDone(); },
  });

  const unsuspendMutation = useMutation({
    mutationFn: () => api.post(`/users/${entityId}/${userId}/unsuspend`),
    onSuccess: () => { setConfirm(null); void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); onDone(); },
  });

  const removeMutation = useMutation({
    mutationFn: () => api.delete(`/users/${entityId}/${userId}`),
    onSuccess: () => { setConfirm(null); void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); onDone(); },
  });

  const clearOnboardingMutation = useMutation({
    mutationFn: () => api.post(`/users/${entityId}/${userId}/clear-onboarding`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  const setRoleMutation = useMutation({
    mutationFn: (entityRole: string) => api.put(`/users/${entityId}/${userId}/role`, { entityRole }),
    onSuccess: () => { setRolePending(null); void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  return (
    <div className="bg-bg-surface border border-white/[0.06] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div>
          <p className="text-sm font-medium text-white truncate">{user.email}</p>
          {user.fullName && <p className="text-xs text-gray-400">{user.fullName}</p>}
        </div>
        <button onClick={onDone} className="text-gray-500 hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Role picker */}
      {!isDeleted && (
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-3">
          <UserCog className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-400 flex-shrink-0">Entity role</span>
          <div className="flex gap-1.5 ml-auto">
            {['member', 'admin'].map((role) => {
              const isActive = (rolePending ?? currentRole) === role;
              return (
                <button
                  key={role}
                  disabled={setRoleMutation.isPending}
                  onClick={() => {
                    if (role !== currentRole) {
                      setRolePending(role);
                      setRoleMutation.mutate(role);
                    }
                  }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'bg-white/[0.06] text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Reset link result */}
      {resetResult && (
        <div className="mx-3 my-2 p-2.5 bg-green-900/20 border border-green-900/40 rounded-lg">
          <p className="text-xs text-green-400 font-medium mb-1">Password reset link generated</p>
          <p className="text-[11px] text-green-300 break-all font-mono">{resetResult}</p>
          <button
            onClick={() => void navigator.clipboard.writeText(resetResult)}
            className="mt-1.5 text-xs text-green-400 underline"
          >
            Copy to clipboard
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="p-2 space-y-0.5">
        <ActionButton
          icon={KeyRound}
          label="Send password reset"
          onClick={() => resetMutation.mutate()}
          loading={resetMutation.isPending}
        />

        {!isDeleted && (isSuspended ? (
          <ActionButton
            icon={ShieldCheck}
            label="Unsuspend account"
            variant="success"
            onClick={() => setConfirm('unsuspend')}
          />
        ) : (
          <ActionButton
            icon={ShieldOff}
            label="Suspend account"
            variant="danger"
            onClick={() => setConfirm('suspend')}
          />
        ))}

        {!isDeleted && (
          <ActionButton
            icon={Trash2}
            label="Remove user"
            variant="danger"
            onClick={() => setConfirm('remove')}
          />
        )}

        <ActionButton
          icon={RotateCcw}
          label={clearOnboardingMutation.isSuccess ? 'Onboarding cleared ✓' : 'Clear onboarding flag'}
          onClick={() => clearOnboardingMutation.mutate()}
          loading={clearOnboardingMutation.isPending}
          disabled={clearOnboardingMutation.isSuccess}
        />
      </div>

      {confirm === 'suspend' && (
        <ConfirmDialog
          title="Suspend this account?"
          body="The user will be immediately logged out and unable to sign in until unsuspended."
          confirmLabel="Suspend"
          danger
          onConfirm={() => suspendMutation.mutate()}
          onCancel={() => setConfirm(null)}
          loading={suspendMutation.isPending}
        />
      )}
      {confirm === 'unsuspend' && (
        <ConfirmDialog
          title="Unsuspend this account?"
          body="The user will be able to sign in again immediately."
          confirmLabel="Unsuspend"
          onConfirm={() => unsuspendMutation.mutate()}
          onCancel={() => setConfirm(null)}
          loading={unsuspendMutation.isPending}
        />
      )}
      {confirm === 'remove' && (
        <ConfirmDialog
          title="Remove this user?"
          body="The account will be disabled and marked as deleted. This action is reversible by an engineer but not from this panel."
          confirmLabel="Remove"
          danger
          onConfirm={() => removeMutation.mutate()}
          onCancel={() => setConfirm(null)}
          loading={removeMutation.isPending}
        />
      )}
    </div>
  );
}

export function Users() {
  const [emailSearch, setEmailSearch] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data, isLoading } = useQuery<{ users: User[] }>({
    queryKey: ['admin', 'users', 'search', submitted],
    queryFn: () => api.get(`/users/search${submitted ? `?email=${encodeURIComponent(submitted)}` : ''}`),
  });

  // Load all entities to resolve names
  const { data: entitiesData } = useQuery<{ entities: Entity[] }>({
    queryKey: ['admin', 'entities'],
    queryFn: () => api.get('/entities'),
  });
  const entityMap = Object.fromEntries((entitiesData?.entities ?? []).map(e => [e.id, e]));

  const users = data?.users ?? [];

  return (
    <div>
      <PageHeader title="Users" subtitle="Cross-entity user search and management" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: search + results */}
        <div className="lg:col-span-2">
          <form
            onSubmit={e => { e.preventDefault(); setSubmitted(emailSearch); setSelectedUser(null); }}
            className="flex gap-2 mb-4"
          >
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by email prefix..."
                value={emailSearch}
                onChange={e => setEmailSearch(e.target.value)}
                className="w-full bg-bg-surface border border-white/[0.06] rounded-xl pl-8 pr-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Search
            </button>
          </form>

          {isLoading ? (
            <LoadingState message="Searching..." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Entity</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <TrEmpty cols={4} message={submitted ? 'No matching users' : 'Enter an email to search'} />
                ) : (
                  users.map(u => {
                    const uid = u.userId ?? u.id ?? '';
                    const entity = entityMap[u.entityId];
                    const isSelected = selectedUser && (selectedUser.userId ?? selectedUser.id) === uid;
                    return (
                      <tr
                        key={`${u.entityId}:${uid}`}
                        onClick={() => setSelectedUser(isSelected ? null : u)}
                        className={`cursor-pointer transition-colors ${isSelected ? 'bg-primary/10' : 'hover:bg-white/[0.02]'}`}
                      >
                        <Td className="font-medium">
                          <div className="flex flex-col">
                            <span>{u.email}</span>
                            {u.fullName && <span className="text-xs text-gray-500">{u.fullName}</span>}
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center gap-2">
                            <Badge variant={ROLE_VARIANT[u.entityRole ?? u.role] ?? 'default'}>
                              {(u.entityRole ?? u.role).replace(/_/g, ' ')}
                            </Badge>
                            {u.suspendedAt && <Badge variant="warning">Suspended</Badge>}
                            {u.deletedAt && <Badge variant="warning">Deleted</Badge>}
                          </div>
                        </Td>
                        <Td>
                          {u.entityId ? (
                            entity ? (
                              <Link
                                to={`/entities/${u.entityId}`}
                                onClick={e => e.stopPropagation()}
                                className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors text-sm"
                              >
                                {entity.name}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : (
                              <span className="font-mono text-xs text-gray-500">{u.entityId}</span>
                            )
                          ) : (
                            <span className="text-gray-500 text-xs">—</span>
                          )}
                        </Td>
                        <Td>
                          <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </div>

        {/* Right: actions panel */}
        <div className="lg:col-span-1">
          {selectedUser ? (
            <UserActions user={selectedUser} onDone={() => setSelectedUser(null)} />
          ) : (
            <div className="border border-white/[0.06] rounded-xl p-6 text-center">
              <p className="text-sm text-gray-500">Select a user to see actions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
